// Legal document content for Traces
// NOTE: This content is a starting template. Have it reviewed by legal counsel before production use.

export const LEGAL_LAST_UPDATED = "February 27, 2026";
export const CONTACT_EMAIL = "hello@trytraces.com";

// ─── Privacy Policy ─────────────────────────────────────────────────────────

export const privacyPolicySections = [
  {
    id: "information-we-collect",
    title: "1. Information We Collect",
    content:
      "When you use Traces, we collect the following types of information:",
    subsections: [
      {
        title: "Account Information",
        content:
          "When you create an account, we collect your name, email address, and password. Your password is stored in hashed form and is never accessible in plain text.",
      },
      {
        title: "Location Data",
        content:
          "Traces is a location-based service. When you grant permission, we collect your device's GPS coordinates to show nearby notes and to attach location information to notes you create. We only collect location data while the app is actively in use and you have granted permission. We never collect location data in the background.",
      },
      {
        title: "User-Generated Content",
        content:
          "We store the notes, comments, and images you create within the app, along with associated metadata such as timestamps and location coordinates.",
      },
      {
        title: "Usage Data",
        content:
          "We may in the future collect basic information about how you interact with the app, such as pages visited and features used, to help us improve the service. If we introduce analytics, we will update this policy accordingly.",
      },
    ],
  },
  {
    id: "how-we-use-your-information",
    title: "2. How We Use Your Information",
    content: "We use the information we collect to:",
    list: [
      "Provide and operate the Traces service, including displaying nearby notes and enabling note creation",
      "Personalize your experience by showing content relevant to your location",
      "Communicate with you about your account, updates, and service-related notices",
      "Improve and develop new features for the app",
      "Detect and prevent fraud, abuse, or other harmful activities",
    ],
  },
  {
    id: "location-data",
    title: "3. Location Data",
    content:
      "Because Traces is built around location, we want to be especially transparent about how we handle your location data:",
    subsections: [
      {
        title: "When We Collect It",
        content:
          "Location data is collected only when you actively use the app and have granted location permission through your browser or device. You can revoke this permission at any time through your browser or device settings.",
      },
      {
        title: "How It's Used",
        content:
          "Your location is used to find and display notes near you, and to attach coordinates to notes you create. Public notes include location data that is visible to other users.",
      },
      {
        title: "Location Precision Controls",
        content:
          "Traces provides location display settings that let you control how precisely your location is shown on public notes. You can choose to display your exact location, or an approximate area.",
      },
      {
        title: "Private Notes",
        content:
          "Notes marked as private are only visible to you. Their location data is never shared with other users.",
      },
    ],
  },
  {
    id: "third-party-services",
    title: "4. Third-Party Services",
    content: "We use the following third-party services to operate Traces:",
    subsections: [
      {
        title: "Mapbox",
        content:
          "We use Mapbox to provide interactive map functionality. When you use the map, Mapbox may collect usage data such as map interactions and approximate location in accordance with their privacy policy.",
      },
      {
        title: "Hosting and Infrastructure",
        content:
          "We use cloud hosting providers to store and serve the app and its data. These providers process data on our behalf and are contractually obligated to protect your information.",
      },
    ],
  },
  {
    id: "data-sharing",
    title: "5. Data Sharing and Disclosure",
    content:
      "We do not sell your personal information. We may share your data only in these circumstances:",
    list: [
      "With your consent or at your direction",
      "With service providers who assist in operating the app (subject to confidentiality obligations)",
      "To comply with legal obligations, such as responding to lawful requests from law enforcement",
      "To protect the rights, safety, or property of Traces, our users, or the public",
      "In connection with a merger, acquisition, or sale of assets (you will be notified of any such change)",
    ],
  },
  {
    id: "data-retention",
    title: "6. Data Retention",
    content:
      "We retain your information for as long as your account is active or as needed to provide you with our services. If you delete your account, we will delete your personal data within 30 days, except where we are required to retain it for legal or regulatory purposes. Publicly shared notes and comments may remain visible even after account deletion, but will be disassociated from your identity.",
  },
  {
    id: "your-rights",
    title: "7. Your Rights and Choices",
    content: "You have the following rights regarding your data:",
    list: [
      "Access — Request a copy of the personal data we hold about you",
      "Correction — Update or correct inaccurate information in your account settings",
      "Deletion — Delete your account and associated data through the app settings",
      "Location Permission — Grant or revoke location access at any time through your browser or device settings",
      "Note Visibility — Control whether your notes are public or private",
    ],
  },
  {
    id: "cookies",
    title: "8. Cookies and Local Storage",
    content:
      "Traces uses a single cookie to store your authentication refresh token. This cookie is essential for keeping you logged in and cannot be disabled while using the service. We also use your browser's local storage to save your preferences (such as theme and display settings). We do not use any tracking or advertising cookies.",
  },
  {
    id: "childrens-privacy",
    title: "9. Children's Privacy",
    content: `Traces involves location sharing and traveling to physical places, so it is not designed for unsupervised use by anyone under 16. Users under 16 may use Traces only if a parent or legal guardian creates and manages their account. Users aged 16 or 17 may have their own account with parental consent, and their parent or guardian must agree to our Terms of Service on their behalf. If you are a parent or guardian and believe your child under 16 has created an account without your involvement, please reach out to us at ${CONTACT_EMAIL} so we can remove their information.`,
  },
  {
    id: "changes",
    title: "10. Changes to This Policy",
    content:
      'We may update this Privacy Policy from time to time. When we make changes, we will update the "Last updated" date at the top of this page. If we make material changes, we will notify you through the app or by email. Your continued use of Traces after changes are posted constitutes your acceptance of the updated policy.',
  },
  {
    id: "contact",
    title: "11. Contact Us",
    content: `If you have questions about this Privacy Policy or how we handle your data, please contact us at ${CONTACT_EMAIL}`,
  },
];

// ─── Terms of Service ───────────────────────────────────────────────────────

export const termsOfServiceSections = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    content:
      "By creating an account or using Traces, you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use the service. We may update these terms from time to time, and your continued use constitutes acceptance of any changes.",
  },
  {
    id: "account-responsibilities",
    title: "2. Account Registration and Responsibilities",
    content: "When you create an account on Traces, you agree to:",
    list: [
      "Provide accurate and complete information during registration",
      "Maintain the security of your account credentials and not share them with others",
      "Notify us immediately if you suspect unauthorized access to your account",
      "Be responsible for all activity that occurs under your account",
    ],
  },
  {
    id: "user-content",
    title: "3. User-Generated Content",
    content:
      "Traces allows you to create notes, comments, and upload images tied to geographic locations.",
    subsections: [
      {
        title: "Ownership",
        content:
          "You retain ownership of the content you create on Traces. By posting public content, you grant Traces a non-exclusive, royalty-free, worldwide license to display, distribute, and make your content available to other users as part of the service.",
      },
      {
        title: "Your Responsibilities",
        content:
          "You are solely responsible for the content you post. You represent that you have the right to share any content you upload and that it does not violate any third party's rights or any applicable laws.",
      },
      {
        title: "Content Removal",
        content:
          "We reserve the right to remove any content that violates these terms or our Community Guidelines, without prior notice.",
      },
    ],
  },
  {
    id: "prohibited-conduct",
    title: "4. Prohibited Conduct",
    content: "When using Traces, you agree not to:",
    list: [
      "Post content that is illegal, harmful, threatening, abusive, harassing, defamatory, sexually explicit, or otherwise objectionable",
      "Impersonate any person or entity, or falsely represent your affiliation with any person or entity",
      "Post spam, unsolicited advertising, or promotional material",
      "Create notes with intentionally false or misleading location data",
      "Attempt to access other users' accounts or private notes without authorization",
      "Use the service to stalk, track, or monitor other individuals",
      "Upload malicious code, viruses, or any content designed to harm the service or its users",
      "Use automated tools (bots, scrapers) to access the service without our written permission",
      "Interfere with or disrupt the service or its infrastructure",
    ],
  },
  {
    id: "location-features",
    title: "5. Location-Based Features",
    content:
      "Traces relies on location data to function. By using the service, you acknowledge that:",
    list: [
      "Location data may not always be perfectly accurate due to GPS limitations, network conditions, or device capabilities",
      "Notes you create with location data reflect the coordinates captured at the time of creation",
      "You should not rely on Traces for navigation, safety-critical decisions, or emergency services",
      "You are responsible for ensuring that notes you leave at locations do not violate any local laws or regulations",
    ],
  },
  {
    id: "intellectual-property",
    title: "6. Intellectual Property",
    content:
      "The Traces name, logo, and all associated branding, design elements, and software are the property of Traces and are protected by applicable intellectual property laws. You may not use our branding without our prior written consent. Your content remains yours as described in Section 3.",
  },
  {
    id: "privacy",
    title: "7. Privacy",
    content:
      "Your use of Traces is also governed by our Privacy Policy, which describes how we collect, use, and protect your information. By using Traces, you consent to the practices described in our Privacy Policy.",
  },
  {
    id: "termination",
    title: "8. Termination",
    content:
      "We may suspend or terminate your account at our discretion if you violate these terms or our Community Guidelines. You may delete your account at any time through the app settings. Upon termination, your right to use the service ceases immediately. We may retain certain information as required by law or for legitimate business purposes.",
  },
  {
    id: "disclaimers",
    title: "9. Disclaimers",
    content:
      'Traces is provided "as is" and "as available" without warranties of any kind, whether express or implied. We do not warrant that the service will be uninterrupted, secure, or error-free. We do not endorse or guarantee the accuracy, reliability, or quality of any user-generated content. You use the service at your own risk.',
  },
  {
    id: "limitation-of-liability",
    title: "10. Limitation of Liability",
    content:
      "To the fullest extent permitted by law, Traces and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the service. Our total liability for any claims arising from the use of Traces shall not exceed the amount you paid us (if any) in the 12 months preceding the claim.",
  },
  {
    id: "governing-law",
    title: "11. Governing Law",
    content:
      "These terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Traces operates, without regard to conflict of law provisions. Any disputes arising from these terms or your use of the service shall be resolved through binding arbitration or in the courts of the applicable jurisdiction.",
  },
  {
    id: "changes",
    title: "12. Changes to These Terms",
    content:
      "We may revise these Terms of Service at any time by updating this page. Material changes will be communicated through the app or by email. Your continued use of Traces after changes take effect constitutes your acceptance of the revised terms.",
  },
  {
    id: "contact",
    title: "13. Contact Us",
    content: `If you have questions about these Terms of Service, please contact us at ${CONTACT_EMAIL}`,
  },
];

// ─── Community Guidelines ───────────────────────────────────────────────────

export const communityGuidelinesSections = [
  {
    id: "our-mission",
    title: "1. Our Mission",
    content:
      "Traces is a community built around sharing experiences tied to real places. These guidelines exist to keep Traces a safe, respectful, and enjoyable space for everyone. By using Traces, you agree to follow these guidelines.",
  },
  {
    id: "be-respectful",
    title: "2. Be Respectful",
    content:
      "Treat others the way you want to be treated. Traces is a shared space, and mutual respect is essential.",
    list: [
      "No harassment, bullying, or intimidation of any kind",
      "No hate speech, slurs, or content that promotes discrimination based on race, ethnicity, gender, sexual orientation, religion, disability, or any other protected characteristic",
      "No personal attacks in comments or notes",
      "Disagree respectfully. Constructive dialogue is welcome, hostility is not",
    ],
  },
  {
    id: "appropriate-content",
    title: "3. Keep Content Appropriate",
    content: "All content on Traces should be suitable for a general audience.",
    list: [
      "No sexually explicit or pornographic content",
      "No graphic violence or gore",
      "No content that promotes or glorifies illegal activities",
      "No content that encourages self-harm or dangerous behavior",
    ],
  },
  {
    id: "location-integrity",
    title: "4. Location Integrity",
    content:
      "Location is at the heart of Traces. The value of the platform depends on notes being genuinely tied to real places.",
    list: [
      "Do not create notes with intentionally fake or misleading locations",
      "Do not use GPS spoofing tools to place notes at locations you haven't visited",
      "Do not create notes that misrepresent a physical location or business",
      "Do not use location features to track, stalk, or monitor other individuals",
    ],
  },
  {
    id: "privacy-of-others",
    title: "5. Respect the Privacy of Others",
    content:
      "Everyone has a right to privacy. Be mindful of others when creating content.",
    list: [
      "Do not share other people's personal information (addresses, phone numbers, real names) without their consent",
      "Do not post photos of identifiable individuals without their permission",
      "Do not create notes intended to surveil or monitor someone's location or activities",
      "Do not use Traces to reveal the private locations of others (homes, workplaces) in a way that could cause harm",
    ],
  },
  {
    id: "no-spam",
    title: "6. No Spam or Commercial Abuse",
    content: "Keep Traces genuine and free from spam.",
    list: [
      "No repetitive, mass-posted, or copy-pasted notes",
      "No unauthorized advertising or commercial solicitation",
      "No misleading or deceptive content designed to drive traffic elsewhere",
      "No bots or automated accounts",
    ],
  },
  {
    id: "reporting",
    title: "7. Reporting and Enforcement",
    content:
      "If you encounter content or behavior that violates these guidelines, please report it. We review all reports and take appropriate action. Reports are confidential; we do not reveal the identity of the reporter to the person being reported.",
  },
  {
    id: "consequences",
    title: "8. Consequences",
    content:
      "Violations of these guidelines may result in the following actions, depending on severity:",
    list: [
      "A warning with an explanation of the violation",
      "Removal of the offending content",
      "Temporary suspension of your account",
      "Permanent ban from the platform",
    ],
    afterContent:
      "Severe violations (such as threats of violence, posting illegal content, or doxxing) may result in immediate permanent bans without prior warning.",
  },
  {
    id: "appeals",
    title: "9. Appeals",
    content: `If you believe your content was removed or your account was suspended in error, you may appeal by contacting us at ${CONTACT_EMAIL}. We will review your appeal and respond within a reasonable timeframe.`,
  },
];
